import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  zip,
} from 'rxjs';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { RadioItemLike } from '../../../radio-group/radio-group.component';

import Graphic from '@arcgis/core/Graphic';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { ToastService } from 'app/shared/services/toast.service';

import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { AuthService } from 'app/shared/services/auth.service';
import SketchWidget from '@arcgis/core/widgets/Sketch';
import { CodedValueItem } from 'app/shared/interfaces/coded-value-item.interface';
import { MapDomainService } from 'app/shared/services/map-domain.service';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import {
  entityIdFieldName,
  entityNameFieldName,
  editableFieldName,
  typeEntityFieldName,
  typeObjectFieldName,
} from 'app/shared/constants/search-results.constant';
import { RoleType } from 'app/shared/models/role-type';
import { ObjectUtils } from 'app/shared/utils/object.utils';
import { EventBusService } from 'app/shared/services/event-bus.service';
import { EntityService } from 'app/shared/services/entity.service';
import { ScreenService } from 'app/shared/services/screen.service';
import { TranslationService } from '@services/translation.service';

interface LayerItemLike extends RadioItemLike {
  layer: __esri.Sublayer;
  featureLayer: __esri.FeatureLayer;
}

interface AttributesFormLike {
  idobjeto?: FormControl<string>;
  distrito?: FormControl<CodedValueItem<string>>;
  concelho?: FormControl<CodedValueItem<string>>;
  freguesia?: FormControl<CodedValueItem<string>>;
  arruamento?: FormControl<string>;
  npolicia?: FormControl<string>;
  tracado?: FormControl<string>;
  afetacaoprincipal?: FormControl<string>;
  diametro?: FormControl<number>;
  comprimento?: FormControl<number>;
  largura?: FormControl<number>;
  altura?: FormControl<number>;
  cota?: FormControl<number>;
  tipoutilizacao?: FormControl<string>;
  estadooperacional?: FormControl<string>;
  estadoocupacao?: FormControl<number>;
  baseinstalacao?: FormControl<string>;
  cdt?: FormControl<string>;
}

@Component({
  selector: 'app-register-element-map',
  templateUrl: 'register-element-map.component.html',
  styleUrls: ['register-element-map.component.scss'],
})
export class RegisterElementMapComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  graphicToEdit: __esri.Graphic;
  @Output()
  graphicToEditChange = new EventEmitter<__esri.Graphic>(true);

  @Input()
  mapView: __esri.MapView;

  @Input()
  mainImageLayer: __esri.MapImageLayer;

  @Output()
  onRegisterSaved = new EventEmitter<Graphic>();

  layerList: LayerItemLike[];
  private _layerItemSelected: LayerItemLike;
  get layerItemSelected() {
    return this._layerItemSelected;
  }

  set layerItemSelected(value) {
    this._layerItemSelected = value;

    this.featureLayerSubject.next(
      this._layerItemSelected?.featureLayer ?? null
    );
  }

  private featureLayerSubject = new BehaviorSubject<__esri.FeatureLayer>(null);

  graphicObj: __esri.Graphic;

  updateRegisterMode = false;
  private _canEdit = false;

  get isAllFieldsReadonly() {
    return !(this.newRegisterMode || this._canEdit);
  }

  get newRegisterMode() {
    return !this.updateRegisterMode && !!this.layerItemSelected;
  }

  get hasGraphicObj() {
    return !!this.graphicObj;
  }

  private formGroupSubject = new BehaviorSubject<FormGroup<AttributesFormLike>>(
    null
  );

  get formGroup() {
    return this.formGroupSubject.getValue();
  }

  private formGroupAttrsSubject = new BehaviorSubject<any>(null);

  private fieldsMap = new Map<string, __esri.Field>();

  districtList: CodedValueItem<string>[];
  countyList: CodedValueItem<string>[];
  parishList: CodedValueItem<string>[];

  get isBtnSubmitDisabled() {
    return (
      !this.formGroup || this.formGroup.invalid || this.isAllFieldsReadonly
    );
  }

  private graphicToEditChangeInternalSubject =
    new BehaviorSubject<__esri.Graphic>(null);
  private graphicToEditFromExternalSubject = new Subject<__esri.Graphic>();

  private sketchWidget: SketchWidget;
  private graphicsLayer: GraphicsLayer;

  private mapViewSubscription: Subscription;
  private mainImageLayerSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private mainMapService: MainMapService,
    private mapDomainService: MapDomainService,
    private entityService: EntityService,
    private authService: AuthService,
    private toastService: ToastService,
    private loadingController: LoadingController,
    private eventBus: EventBusService,
    private screenService: ScreenService,
    private ts: TranslationService
  ) {}

  ngOnInit(): void {
    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.goBackStep();
      });

    this.graphicToEditChangeInternalSubject
      .pipe(
        switchMap((graphic) => {
          if (graphic) {
            this.sketchWidget.update(graphic);
          }

          return this.canEditItemObservable(graphic);
        }),
        distinctUntilChanged(),
        takeUntil(this.lifecycle)
      )
      .subscribe((aBool) => {
        this._canEdit = aBool;
      });

    this.featureLayerSubject
      .pipe(
        switchMap((layer) => {
          if (!layer) {
            return of(null);
          }

          this.loadingController.show();
          return EsriUtils.load(layer).pipe(
            finalize(() => {
              this.loadingController.hide();
            }),
            map(() => layer),
            switchMap((layer) => {
              this.handleFormGroup(layer);

              if (layer) {
                this.startCreationMode();
              }

              return this.formGroupSubject.pipe(
                finalize(() => {
                  this.formGroupSubject.next(null);
                }),
                switchMap((formGroup) => {
                  return this.formGroupAttrsSubject.pipe(
                    tap((attrs) => {
                      formGroup?.reset(attrs ?? undefined);
                    })
                  );
                })
              );
            })
          );
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView) {
      this.handleMapView();
    }

    if (changes.mainImageLayer) {
      this.handleMainImageLayer();
    }

    if (changes.graphicToEdit) {
      this.graphicToEditFromExternalSubject.next(this.graphicToEdit);
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.mapViewSubscription?.unsubscribe();
    this.mainImageLayerSubscription?.unsubscribe();

    this.goBackStep();
  }

  onSubmit(e) {
    e?.preventDefault();
    if (this.loadingController.isShown() || this.isBtnSubmitDisabled) {
      return;
    }

    const rawValue = this.formGroup.getRawValue();
    const locationFields: Array<keyof AttributesFormLike> = [
      'distrito',
      'concelho',
      'freguesia',
    ];
    locationFields.forEach((field) => {
      if (field in rawValue) {
        rawValue[field as any] = (
          rawValue[field] as CodedValueItem<string>
        )?.label;
      }
    });

    const graphicObj = this.graphicObj;
    const attributes = (graphicObj.attributes = Object.assign(
      graphicObj.attributes ?? {},
      rawValue
    ));

    const layer = this.layerItemSelected.layer;
    const featureLayer = this.layerItemSelected.featureLayer;

    let observable: Observable<any>;
    if (attributes[layer.objectIdField]) {
      observable = EsriUtils.applyEdits(featureLayer, {
        updateFeatures: [graphicObj],
      });
    } else {
      observable = this.authService.getEntityId().pipe(
        mergeMap((entityId) => {
          attributes[entityIdFieldName] = entityId;

          return this.entityService.getEntityById(entityId).pipe(
            mergeMap((entity) => {
              attributes[entityNameFieldName] = entity?.name;
              attributes[typeEntityFieldName] = entity?.typeName;

              return EsriUtils.applyEdits(featureLayer, {
                addFeatures: [graphicObj],
              });
            })
          );
        })
      );
    }

    this.loadingController.show();
    observable
      .pipe(
        finalize(() => {
          this.loadingController.hide();
        }),
        takeUntil(this.lifecycle)
      )
      .subscribe({
        next: () => {
          this.toastService.success(
            this.ts.getCurrentLanguage()['data-saved-successfully']
          );

          this.mainImageLayer?.refresh();

          this.goBackStep();

          this.onRegisterSaved.emit(graphicObj);
        },
        error: (response) => {
          if (!response) {
            return;
          }

          this.toastService.error(
            this.ts.getCurrentLanguage()['failed-save-data']
          );
        },
      });
  }

  onBtnClearForm() {
    this.formGroupAttrsSubject.next(null);
  }

  onBtnGoBackClick() {
    this.goBackStep();
  }

  fieldExists(fieldName: string) {
    return this.fieldsMap.has(fieldName);
  }

  isFieldRequired(fieldName: string) {
    return !this.fieldsMap.get(fieldName)?.nullable;
  }

  getFieldAlias(fieldName: string) {
    return this.fieldsMap.get(fieldName)?.alias ?? fieldName;
  }

  private goBackStep() {
    this.sketchWidget?.cancel();
    this.graphicsLayer?.removeAll();
    this.layerItemSelected = null;
    this.updateRegisterMode = false;
    this.graphicObj = null;
    this.formGroupAttrsSubject.next(null);
    this.graphicToEditChangeInternalSubject.next(null);

    if (this.graphicToEdit) {
      this.graphicToEdit = null;
      this.graphicToEditChange.emit(this.graphicToEdit);

      this.eventBus.emit('register-element.edit-finish');
    }
  }

  private handleFormGroup(layer: __esri.FeatureLayer) {
    this.fieldsMap.clear();

    let formGroup: FormGroup<AttributesFormLike>;
    if (layer) {
      layer.fields?.forEach((field) => {
        this.fieldsMap.set(field.name, field);
      });

      const keys: Array<keyof AttributesFormLike> = [
        'idobjeto',
        'distrito',
        'concelho',
        'freguesia',
        'arruamento',
        'npolicia',
        'tracado',
        'afetacaoprincipal',
        'diametro',
        'comprimento',
        'largura',
        'altura',
        'cota',
        'tipoutilizacao',
        'estadooperacional',
        'estadoocupacao',
        'baseinstalacao',
        'cdt',
      ];
      const attrsForm: AttributesFormLike = {};
      for (const key of keys) {
        if (this.fieldExists(key)) {
          attrsForm[key] = this.formBuilder.control(
            null,
            this.isFieldRequired(key) ? [Validators.required] : []
          ) as any;
        }
      }

      formGroup = this.formBuilder.group<AttributesFormLike>(attrsForm);
    }

    this.formGroupSubject.next(formGroup ?? null);
  }

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView) {
      return;
    }

    const { mapView } = this;

    const subscription = new Subscription();

    this.graphicsLayer = new GraphicsLayer();
    mapView.map.add(this.graphicsLayer);

    this.sketchWidget = new SketchWidget({
      layer: this.graphicsLayer,
      view: mapView,
      creationMode: 'single',
    });

    this.mainMapService.disableMapPopup();
    subscription.add(() => {
      this.mainMapService.enableMapPopup();

      if (this.sketchWidget) {
        this.sketchWidget.cancel();
        this.sketchWidget.destroy();
        this.sketchWidget = null;
      }

      if (this.graphicsLayer) {
        mapView.map.remove(this.graphicsLayer);
        this.graphicsLayer.destroy();
        this.graphicsLayer = null;
      }
    });

    subscription.add(
      EsriUtils.on(this.sketchWidget, 'create')
        .pipe(takeUntil(this.lifecycle))
        .subscribe((evt: __esri.SketchCreateEvent) => {
          switch (evt.state) {
            case 'complete':
              this.graphicObj = evt.graphic;
              break;
          }
        })
    );

    const graphicToEditFromMapClickObservable = EsriUtils.leftClicks(
      mapView
    ).pipe(
      filter(
        () =>
          !(
            this.hasGraphicObj &&
            (this.updateRegisterMode || this.newRegisterMode)
          )
      ),
      map((e) => e.mapPoint as __esri.Point),
      switchMap((mapPoint) => {
        const scale = this.mapView.scale;

        const layerIds = (EsriUtils.getDeeperLayers([this.mainImageLayer])
          ?.filter((x) => EsriUtils.isRecursiveLayerVisibleAndInScale(x, scale))
          ?.reverse()
          ?.map((x) => x.id) ?? []) as number[];

        const parameters = new IdentifyParameters({
          geometry: mapPoint,
          tolerance: 20,
          width: mapView.width,
          height: mapView.height,
          layerOption: 'visible',
          layerIds,
          mapExtent: mapView.extent,
          spatialReference: mapView.spatialReference,
          returnGeometry: true,
          returnUnformattedValues: true,
        });

        return EsriUtils.identify(this.mainImageLayer.url, parameters).pipe(
          map((res) => {
            const item = res?.results?.[0];
            if (item) {
              this.layerItemSelected = this.layerList.find(
                (x) => x.layer.id === item.layerId
              );

              return item.feature;
            }

            return null;
          })
        );
      })
    );

    const graphicToEditFromExternalObservable =
      this.graphicToEditFromExternalSubject.pipe(
        switchMap((graphic) => {
          if (graphic) {
            if (graphic.layer) {
              this.layerItemSelected = this.layerList.find((x) => {
                const layerId =
                  (graphic.layer as __esri.FeatureLayer).layerId ??
                  (graphic.layer as any).id;
                return layerId === x.layer.id;
              });

              if (!this.layerItemSelected) {
                this.layerItemSelected = this.layerList.find((x) => {
                  return (
                    graphic.layer.title?.toLowerCase() ===
                    x.layer.title?.toLowerCase()
                  );
                });
              }
            }

            if (!this.layerItemSelected) {
              const typeObject =
                graphic.attributes[typeObjectFieldName]?.toLowerCase();
              this.layerItemSelected = this.layerList.find((x) => {
                const aux =
                  (
                    x.layer.getFieldDomain(
                      typeObjectFieldName
                    ) as __esri.CodedValueDomain
                  )?.codedValues?.find((y) => {
                    return y.code === graphic.attributes[typeObjectFieldName];
                  })?.name ?? typeObject;

                return aux === x.layer.title?.toLowerCase();
              });
            }

            if (!graphic.geometry && this.layerItemSelected) {
              const layer = this.layerItemSelected.layer;
              const query = layer.createQuery();
              query.outFields = [layer.objectIdField];
              query.where = `${layer.objectIdField}=${
                graphic.attributes[layer.objectIdField]
              }`;
              query.num = 1;
              query.returnGeometry = true;

              this.loadingController.show();
              return EsriUtils.queryFeatures(layer, query).pipe(
                finalize(() => {
                  this.loadingController.hide();
                }),
                map((featureSet) => {
                  const geometry = featureSet?.features?.[0]?.geometry;
                  graphic.geometry = geometry;
                  return graphic;
                })
              );
            }
          }

          return of(graphic);
        })
      );

    subscription.add(
      merge(
        graphicToEditFromMapClickObservable,
        graphicToEditFromExternalObservable
      )
        .pipe(
          switchMap((graphic) => {
            if (!graphic) {
              return of(null);
            }

            const layer = this.layerItemSelected.layer;

            const symbol = (layer.renderer as __esri.SimpleRenderer)?.symbol;

            this.sketchWidget.viewModel.pointSymbol = symbol as any;
            this.sketchWidget.viewModel.polylineSymbol = symbol as any;
            this.sketchWidget.viewModel.polygonSymbol = symbol as any;

            graphic = new Graphic({
              attributes: ObjectUtils.clone(graphic.attributes) ?? {},
              geometry: ObjectUtils.clone(graphic.geometry),
              symbol,
            });

            const values = graphic.attributes;

            const observableList: Observable<any>[] = [of(null)];

            const auxList: Array<
              [string, (x: string) => Observable<CodedValueItem<string>>]
            > = [
              [
                'distrito',
                (x) => this.mapDomainService.getDistrictValueByLabel(x),
              ],
              [
                'concelho',
                (x) =>
                  this.mapDomainService.getCountyValueByLabel(
                    values['distrito']?.value,
                    x
                  ),
              ],
              [
                'freguesia',
                (x) =>
                  this.mapDomainService.getParishValueByLabel(
                    values['concelho']?.value,
                    x
                  ),
              ],
            ];

            const handleLocationValues = (start = 0) => {
              for (let i = start; i < auxList.length; ++i) {
                const [fieldName, fn] = auxList[i];
                if (fieldName in values) {
                  return fn(values[fieldName]).pipe(
                    mergeMap((item) => {
                      values[fieldName] = item;

                      const observable = handleLocationValues(i + 1);
                      if (!observable) {
                        return of(null);
                      }

                      return observable;
                    })
                  );
                }
              }
            };

            const observable = handleLocationValues();
            if (observable) {
              observableList.push(observable);
            }

            this.loadingController.show();
            return zip(observableList).pipe(
              finalize(() => {
                this.loadingController.hide();
              }),
              map(() => {
                return graphic;
              })
            );
          }),
          takeUntil(this.lifecycle)
        )
        .subscribe((graphic) => {
          this.updateRegisterMode = !!graphic;
          this.graphicObj = graphic;

          this.graphicsLayer.removeAll();

          if (graphic) {
            this.formGroupAttrsSubject.next(graphic.attributes);

            this.graphicsLayer.add(graphic);
          }

          this.graphicToEditChangeInternalSubject.next(graphic);
        })
    );

    this.mapViewSubscription = subscription;
  }

  private handleMainImageLayer() {
    this.mainImageLayerSubscription?.unsubscribe();
    this.fieldsMap.clear();

    const layerList = this.getAllSublayersWithIconAsSelectableItem();
    this.layerList = layerList;
    this.layerItemSelected = null;

    if (!this.mainImageLayer) {
      return;
    }

    const subscription = new Subscription();
    subscription.add(() => {
      layerList?.forEach((item) => {
        item?.featureLayer?.destroy();
      });
    });

    subscription.add(this.loadDistrictList());

    const observeLocationValueChanges = (
      fieldName: string,
      callback: (value: string) => Subscription
    ) => {
      return this.observeFormValueChanges(fieldName)
        .pipe(
          switchMap((value) => {
            return new Observable((subscriber) => {
              subscriber.add(
                callback((value as CodedValueItem<string>)?.value)
              );
            });
          })
        )
        .subscribe();
    };

    subscription.add(
      observeLocationValueChanges('distrito', (value) => {
        return this.loadCountyList(value);
      })
    );

    subscription.add(
      observeLocationValueChanges('concelho', (value) => {
        return this.loadParishList(value);
      })
    );

    this.mainImageLayerSubscription = subscription;
  }

  private startCreationMode() {
    const item = this.layerItemSelected;
    const layer = this.layerItemSelected.layer;
    const symbol = (layer.renderer as __esri.SimpleRenderer)?.symbol;

    if (this.sketchWidget) {
      this.sketchWidget.viewModel.pointSymbol = symbol as any;
      this.sketchWidget.viewModel.polylineSymbol = symbol as any;
      this.sketchWidget.viewModel.polygonSymbol = symbol as any;

      switch (item.featureLayer.geometryType) {
        case 'point':
        case 'multipoint':
          this.sketchWidget.create('point');
          break;
        case 'polygon':
          this.sketchWidget.create('polygon');
          break;
        case 'polyline':
          this.sketchWidget.create('polyline');
          break;
      }
    }
  }

  private getAllSublayersWithIconAsSelectableItem() {
    return (
      (EsriUtils.getDeeperLayers(this.mainImageLayer) as __esri.Sublayer[])
        ?.map((layer) => {
          const symbol = (layer.renderer as __esri.SimpleRenderer)?.symbol;

          if (!symbol) {
            return null;
          }

          const item: LayerItemLike = {
            label: layer.title,
            iconAs: 'svg',
            layer,
            featureLayer: new FeatureLayer({
              url: layer.url.replace('MapServer', 'FeatureServer'),
            }),
          };

          EsriUtils.renderSymbolPreviewImageUrl(symbol).then((icon) => {
            item.icon = icon;
          });

          return item;
        })
        ?.filter((x) => !!x)
        ?.reverse() ?? []
    );
  }

  private observeFormValueChanges<T = any>(fieldName: string): Observable<T> {
    return this.formGroupSubject.asObservable().pipe(
      switchMap((formGroup) => {
        if (!formGroup) {
          return EMPTY;
        }

        const control = formGroup.get(fieldName);
        const initialValue = control.value;
        return control.valueChanges.pipe(startWith(initialValue));
      }),
      distinctUntilChanged()
    );
  }

  private loadDistrictList() {
    return this.mapDomainService.getDistrictDomainList().subscribe((list) => {
      this.districtList = list;
    });
  }

  private loadCountyList(district: string) {
    return this.mapDomainService
      .getCountyDomainList(district)
      .subscribe((list) => {
        this.countyList = list;
      });
  }

  private loadParishList(county: string) {
    return this.mapDomainService
      .getParishDomainList(county)
      .subscribe((list) => {
        this.parishList = list;
      });
  }

  getMaxLength(fieldName: string): number {
    return this.fieldsMap.get(fieldName)?.length;
  }

  getDomainList(fieldName: string) {
    return (this.fieldsMap.get(fieldName)?.domain as __esri.CodedValueDomain)
      ?.codedValues;
  }

  private canEditItemObservable(item: __esri.Graphic) {
    let observable: Observable<boolean>;
    if (!item?.attributes?.[editableFieldName]) {
      observable = of(false);
    } else {
      observable = this.authService.observeAuthInfo().pipe(
        map((authInfo) => {
          return authInfo?.entityId === item.attributes[entityIdFieldName];
        }),
        switchMap((aBool) => {
          if (!aBool) {
            return of(aBool);
          }

          return this.authService.observeHasAnyRoleTypes(RoleType.Provider);
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        }),
        distinctUntilChanged()
      );
    }

    return observable;
  }
}
